<template>
    <div class="ppv2-space-y-6 ppv2-pb-2">
        <!-- 1. L'URL du serveur — propre à ce tenant, elle vient du serveur (hôte servi),
             jamais reconstruite côté Vue à partir de window.location. -->
        <section>
            <h4 class="mcp-section-title">{{ $t('configuration.integration.mcpUrlTitle') }}</h4>
            <p class="mcp-hint">{{ $t('configuration.integration.mcpUrlHint') }}</p>
            <div class="mcp-copy-row">
                <code class="mcp-code">{{ url || '…' }}</code>
                <button type="button" class="mcp-copy-btn" :disabled="!url" @click="copy(url, 'url')">
                    {{ copied === 'url' ? $t('configuration.integration.mcpCopied') : $t('configuration.integration.mcpCopy') }}
                </button>
            </div>
        </section>

        <!-- 2. Émission d'un jeton -->
        <section>
            <h4 class="mcp-section-title">{{ $t('configuration.integration.mcpTokenTitle') }}</h4>
            <p class="mcp-hint">{{ $t('configuration.integration.mcpTokenHint', { days: ttlDays }) }}</p>

            <div class="mcp-generate-row">
                <input
                    v-model="newTokenName"
                    type="text"
                    maxlength="60"
                    class="mcp-input"
                    :placeholder="$t('configuration.integration.mcpTokenNamePlaceholder')"
                    @keyup.enter="generate"
                />
                <select
                    v-model="newTokenAccess"
                    class="mcp-input mcp-select"
                    :aria-label="$t('configuration.integration.mcpColAccess')"
                >
                    <option value="read">{{ $t('configuration.integration.mcpAccessRead') }}</option>
                    <option value="read_write">{{ $t('configuration.integration.mcpAccessReadWrite') }}</option>
                </select>
                <BaseButton
                    variant="primary"
                    :disabled="isGenerating || !newTokenName.trim()"
                    :customClass="`!ppv2-h-[42px] !ppv2-min-w-[160px] !ppv2-rounded-xl !ppv2-bg-[#2D246E]`"
                    @click="generate"
                >
                    {{ isGenerating ? $t('common.saving') : $t('configuration.integration.mcpGenerate') }}
                </BaseButton>
            </div>
            <p class="mcp-hint mcp-hint--below">
                {{
                    newTokenAccess === 'read'
                        ? $t('configuration.integration.mcpAccessReadHint')
                        : $t('configuration.integration.mcpAccessReadWriteHint')
                }}
            </p>

            <!-- Affiché UNE seule fois : le serveur ne stocke qu'un hash. -->
            <div v-if="plainTextToken" class="mcp-reveal">
                <div class="mcp-reveal-warning">⚠ {{ $t('configuration.integration.mcpTokenOnce') }}</div>
                <div class="mcp-copy-row">
                    <code class="mcp-code mcp-code--token">{{ plainTextToken }}</code>
                    <button type="button" class="mcp-copy-btn" @click="copy(plainTextToken, 'token')">
                        {{
                            copied === 'token'
                                ? $t('configuration.integration.mcpCopied')
                                : $t('configuration.integration.mcpCopy')
                        }}
                    </button>
                </div>
            </div>
        </section>

        <!-- 3. Jetons actifs -->
        <section v-if="tokens.length">
            <h4 class="mcp-section-title">{{ $t('configuration.integration.mcpActiveTokens') }}</h4>
            <table class="mcp-table">
                <thead>
                    <tr>
                        <th>{{ $t('configuration.integration.mcpColName') }}</th>
                        <th>{{ $t('configuration.integration.mcpColAccess') }}</th>
                        <th>{{ $t('configuration.integration.mcpColOwner') }}</th>
                        <th>{{ $t('configuration.integration.mcpColLastUsed') }}</th>
                        <th>{{ $t('configuration.integration.mcpColExpires') }}</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="token in tokens" :key="token.id" :class="{ 'mcp-row--expired': token.expired }">
                        <td>{{ token.name }}</td>
                        <td>
                            <span :class="token.access === 'read' ? 'mcp-badge-read' : 'mcp-badge-write'">{{
                                token.access === 'read'
                                    ? $t('configuration.integration.mcpAccessRead')
                                    : $t('configuration.integration.mcpAccessReadWrite')
                            }}</span>
                        </td>
                        <td>{{ token.owner }}</td>
                        <td>
                            {{ token.last_used_at ? formatDate(token.last_used_at) : $t('configuration.integration.mcpNever') }}
                        </td>
                        <td>
                            {{ formatDate(token.expires_at) }}
                            <span v-if="token.expired" class="mcp-badge-expired">{{
                                $t('configuration.integration.mcpExpired')
                            }}</span>
                        </td>
                        <td class="ppv2-text-right">
                            <button
                                type="button"
                                class="mcp-revoke-btn"
                                :disabled="revokingId === token.id"
                                @click="revoke(token)"
                            >
                                {{
                                    confirmingId === token.id
                                        ? $t('configuration.integration.mcpConfirmRevoke')
                                        : $t('configuration.integration.mcpRevoke')
                                }}
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </section>

        <!-- 4. Mode d'emploi. claude.ai interdit `authorization` dans ses en-têtes
             personnalisés, d'où x-api-key ; partout ailleurs le Bearer standard passe. -->
        <section>
            <h4 class="mcp-section-title">{{ $t('configuration.integration.mcpHowToTitle') }}</h4>

            <div class="mcp-howto">
                <div class="mcp-howto-name">claude.ai</div>
                <ol class="mcp-steps">
                    <li>{{ $t('configuration.integration.mcpClaudeStep1') }}</li>
                    <li>
                        {{ $t('configuration.integration.mcpClaudeStep2') }} <code>{{ url }}</code>
                    </li>
                    <li>{{ $t('configuration.integration.mcpClaudeStep3') }}</li>
                    <li>
                        {{ $t('configuration.integration.mcpClaudeStep4') }}
                        <code>x-api-key</code>
                    </li>
                </ol>
            </div>

            <div class="mcp-howto">
                <div class="mcp-howto-name">ChatGPT · Claude Desktop · API</div>
                <p class="mcp-hint ppv2-mb-2">{{ $t('configuration.integration.mcpBearerHint') }}</p>
                <code class="mcp-code mcp-code--block"
                    >Authorization: Bearer &lt;{{ $t('configuration.integration.mcpYourToken') }}&gt;</code
                >
            </div>
        </section>
    </div>
</template>

<script>
import axios from 'axios'
import BaseButton from '../../../Shared/BaseButton.vue'

export default {
    name: 'McpIntegration',
    components: { BaseButton },
    data() {
        return {
            url: '',
            ttlDays: 365,
            tokens: [],
            newTokenName: '',
            // Moindre privilège par défaut : l'écriture se choisit explicitement.
            newTokenAccess: 'read',
            plainTextToken: '',
            isGenerating: false,
            revokingId: null,
            confirmingId: null,
            copied: '',
        }
    },
    mounted() {
        this.fetchSettings()
    },
    methods: {
        async fetchSettings() {
            try {
                const { data } = await axios.get('/back-office/v3/integration/mcp')
                this.url = data.url
                this.ttlDays = data.ttl_days
                this.tokens = data.tokens
            } catch (error) {
                this.$emit('error', error.response?.data?.message || this.$t('configuration.integration.error'))
            }
        },
        async generate() {
            const name = this.newTokenName.trim()
            if (!name || this.isGenerating) return

            this.isGenerating = true
            try {
                const { data } = await axios.post('/back-office/v3/integration/mcp/tokens', {
                    name,
                    access: this.newTokenAccess,
                })
                this.plainTextToken = data.plain_text_token
                this.tokens = data.tokens
                this.newTokenName = ''
                this.$toast.success(data.message)
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('configuration.integration.error'))
            } finally {
                this.isGenerating = false
            }
        },
        async revoke(token) {
            // Premier clic = demande de confirmation en place. Pas de confirm() natif :
            // il gèle la modale et n'est pas traduisible.
            if (this.confirmingId !== token.id) {
                this.confirmingId = token.id
                return
            }

            this.revokingId = token.id
            try {
                const { data } = await axios.delete(`/back-office/v3/integration/mcp/tokens/${token.id}`)
                this.tokens = data.tokens
                // Le jeton affiché juste au-dessus vient peut-être d'être révoqué : ne pas
                // laisser à l'écran un secret qui ne vaut plus rien.
                this.plainTextToken = ''
                this.$toast.success(data.message)
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('configuration.integration.error'))
            } finally {
                this.revokingId = null
                this.confirmingId = null
            }
        },
        async copy(value, what) {
            try {
                await navigator.clipboard.writeText(value)
                this.copied = what
                setTimeout(() => {
                    this.copied = ''
                }, 2000)
            } catch (error) {
                this.$toast.error(this.$t('configuration.integration.mcpCopyError'))
            }
        },
        formatDate(iso) {
            if (!iso) return '—'
            return new Date(iso).toLocaleDateString(this.$i18n.locale === 'en' ? 'en-GB' : 'fr-FR')
        },
    },
}
</script>

<style scoped>
.mcp-section-title {
    font-size: 15px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 4px;
}
.mcp-hint {
    font-size: 13px;
    color: #64748b;
    margin-bottom: 10px;
}
.mcp-copy-row {
    display: flex;
    align-items: stretch;
    gap: 8px;
}
.mcp-code {
    flex: 1;
    display: block;
    padding: 10px 12px;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 13px;
    color: #1e293b;
    overflow-wrap: anywhere;
}
.mcp-code--token {
    background: #fff7ed;
    border-color: #fdba74;
}
.mcp-code--block {
    display: block;
}
.mcp-copy-btn,
.mcp-revoke-btn {
    padding: 0 14px;
    border: 1px solid #2d246e;
    border-radius: 10px;
    background: #fff;
    color: #2d246e;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
}
.mcp-copy-btn:disabled,
.mcp-revoke-btn:disabled {
    opacity: 0.5;
}
.mcp-revoke-btn {
    padding: 4px 10px;
    border-color: #c0392b;
    color: #c0392b;
}
.mcp-generate-row {
    display: flex;
    gap: 8px;
    align-items: center;
}
.mcp-input {
    flex: 1;
    height: 42px;
    padding: 0 12px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    font-size: 14px;
}
.mcp-reveal {
    margin-top: 12px;
}
.mcp-reveal-warning {
    font-size: 13px;
    font-weight: 600;
    color: #b45309;
    margin-bottom: 6px;
}
.mcp-table {
    width: 100%;
    font-size: 13px;
    border-collapse: collapse;
}
.mcp-table th {
    text-align: left;
    font-weight: 600;
    color: #64748b;
    padding: 6px 8px;
    border-bottom: 1px solid #e2e8f0;
}
.mcp-table td {
    padding: 8px;
    border-bottom: 1px solid #f1f5f9;
    color: #1e293b;
}
.mcp-row--expired td {
    color: #94a3b8;
}
.mcp-select {
    flex: 0 0 auto;
    background: #fff;
}
.mcp-hint--below {
    margin: 8px 0 0;
}
.mcp-badge-read,
.mcp-badge-write {
    padding: 1px 8px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
}
.mcp-badge-read {
    background: #e0f2fe;
    color: #0369a1;
}
.mcp-badge-write {
    background: #fef3c7;
    color: #92400e;
}
.mcp-badge-expired {
    margin-left: 6px;
    padding: 1px 6px;
    border-radius: 6px;
    background: #fee2e2;
    color: #b91c1c;
    font-size: 11px;
    font-weight: 600;
}
.mcp-howto {
    margin-top: 12px;
    padding: 12px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
}
.mcp-howto-name {
    font-size: 13px;
    font-weight: 700;
    color: #2d246e;
    margin-bottom: 6px;
}
.mcp-steps {
    margin: 0;
    /* Le reset global du BO supprime les puces : la numérotation est ré-affirmée ici,
       ce sont des étapes à suivre dans l'ordre. */
    list-style: decimal outside;
    padding-left: 18px;
    font-size: 13px;
    color: #334155;
}
.mcp-steps li {
    margin-bottom: 3px;
}
.mcp-steps code,
.mcp-howto code {
    padding: 1px 5px;
    background: #e2e8f0;
    border-radius: 5px;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
}
</style>
